import React, { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  H2,
  Button,
  H4,
  Input,
  Close,
  Line,
  Flex,
  SelectDropdown,
  InputModal,
  Span,
  ToolTip,
  ToolTipBlack,
} from '../../lib/components'
import { InvestorListPresenterProps } from '../../lib/types'
import { EditIcon } from '../../lib/components/Icon'
import {
  investorFilter,
  investorAccountStatusFilter,
  investorSort,
  kycStatusMap,
} from '../../lib/utils/message'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { Pagination } from '../../lib/components/Pagination'
import { displayDollar, getTableClassNames, getToolTipText } from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const InvestorsListPresenter = ({
  investors,
  investorData,
  currentPage,
  pagination,
  searchParams,
  handleCurrentPage,
  handleSearchParams,
  resetSearchParams,
  handleInvestorDataChange,
  handleInvestorProfileClick,
  handleInvestorInvestmentsClick,
  handleInvestorWithdrawalClick,
  handleUpdateInvestmentLimit,
}: InvestorListPresenterProps) => {
  const { breakpoint } = useBreakpoint()
  const [displayModal, setDisplayModal] = useState(false)

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Clients' />
      {displayModal ? (
        <InputModal>
          <Flex
            variant='column'
            classes={['input-modal']}
            dataTestId='investor-list-edit-investment-limit-modal'
          >
            <Flex variant='space-between'>
              <H4 text={'Investment Limit'} />
              <Close height='24px' onClick={() => setDisplayModal(false)} dataTestId='close-icon' />
            </Flex>
            <Line />
            <Flex variant='column' classes={['reply-section']}>
              <Input
                name='investmentLimit'
                placeholder='Investment Limit'
                type='number'
                required
                value={
                  (investorData &&
                    investorData.investment_limit &&
                    investorData.investment_limit) ||
                  ''
                }
                handleChange={(e) =>
                  investorData &&
                  handleInvestorDataChange({
                    ...investorData,
                    investment_limit: e.target.value,
                  })
                }
                dataTestId='investment-list-edit-investment-limit-input'
              />
              <Button
                variant='primary-btn'
                text='Save'
                handleClick={() => {
                  investorData &&
                    handleUpdateInvestmentLimit(
                      investorData.investor_id,
                      Number(investorData.investment_limit),
                    )
                  setDisplayModal(false)
                }}
              />
            </Flex>
          </Flex>
        </InputModal>
      ) : null}

      <div className='investor-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          handleChange={(e) => handleSearchParams({ name: 'keyword', value: e.target.value })}
          placeholder='Search'
        />
        <SelectDropdown
          name='filterBy'
          id='filterId'
          formId=''
          list={investorFilter}
          value={searchParams && searchParams.filterBy}
          placeholder={'Filter'}
          handleChange={(val) => handleSearchParams({ name: 'filterBy', value: val })}
          dataTestId='investor-list-filter-list-dropdown'
        />
        <SelectDropdown
          name='account_status'
          id='accountStatusId'
          formId=''
          list={investorAccountStatusFilter}
          value={searchParams && searchParams.account_status}
          placeholder={'Account Status'}
          handleChange={(val) => handleSearchParams({ name: 'account_status', value: val })}
          dataTestId='investor-list-account-status-dropdown'
        />
        <div className='signup-date-filters'>
          <label htmlFor='investor-signup-from'>From</label>
          <input
            id='investor-signup-from'
            type='date'
            name='signupFrom'
            value={searchParams.signupFrom || ''}
            onChange={(e) => handleSearchParams({ name: 'signupFrom', value: e.target.value })}
            data-testid='investor-list-signup-from'
          />
          <label htmlFor='investor-signup-to'>To</label>
          <input
            id='investor-signup-to'
            type='date'
            name='signupTo'
            value={searchParams.signupTo || ''}
            onChange={(e) => handleSearchParams({ name: 'signupTo', value: e.target.value })}
            data-testid='investor-list-signup-to'
          />
        </div>
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={investorSort}
            placeholder={'Sort'}
            value={searchParams.sortBy}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='investor-list-sort-dropdown'
          />
          <Button
            handleClick={resetSearchParams}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      {investors && investors.length ? (
        <div className='w-100'>
          <Flex
            variant='row'
            classes={['mb-24', 'table_scroll_horizontal']}
            dataTestId='investor-list-table-container'
          >
            <table className='styled-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Account Status</th>
                  <th>Client ID</th>
                  <th>NDA Status</th>
                  <th>KYC Status</th>
                  <th>Used Investment Limit</th>
                  <th>Investment Limit</th>
                  <th>Manage Profile / KYC</th>
                  <th>Investment Status</th>
                  <th>Redemption Status</th>
                </tr>
              </thead>
              <tbody>
                {investors.map((investor) => {
                  return (
                    <tr
                      key={uuidv4()}
                      className={getTableClassNames({
                        investor_account_status: investor.account_status,
                      })}
                    >
                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span>{investor.name}</span>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <div
                          className={'table-cell-with-icon'}
                          data-testid='investor-table-email-cell'
                        >
                          {investor?.is_verified ? (
                            <ToolTipBlack variant='check-circle-icon' classes={['mr-8']}>
                              Email Verified
                            </ToolTipBlack>
                          ) : (
                            <ToolTipBlack variant='report-icon' classes={['mr-8']}>
                              Email Not Verified
                            </ToolTipBlack>
                          )}
                          {investor.email}
                        </div>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span>{investor.account_status}</span>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span>{investor.investor_reference}</span>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span>{investor.is_nda_accepted ? 'Accepted' : 'Pending'}</span>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span>
                          {investor?.status ? kycStatusMap[investor.status] : kycStatusMap.PENDING}
                        </span>
                      </ToolTip>

                      <ToolTip
                        variant='table-cell'
                        content={getToolTipText({
                          investor_account_status: investor.account_status,
                        })}
                      >
                        <span data-testid='investor-table-used-investment-limit-cell'>
                          {displayDollar(
                            investor.total_investment ? investor.total_investment?.toString() : '',
                            'USD',
                            2,
                            true,
                          )}
                        </span>
                      </ToolTip>

                      <td>
                        <span data-testid='investor-table-investment-limit-cell'>
                          {displayDollar(
                            investor.investment_limit ? investor.investment_limit.toString() : '',
                            'USD',
                            2,
                            true,
                          )}
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          <EditIcon
                            classes={['align-center', 'pointer']}
                            height='16px'
                            width='16px'
                            onClick={() => {
                              logCrmEvent(EngagementEvents.CLICKED_EDIT_INVESTMENT_LIMIT, {
                                investorEmail: investor.email,
                                investorId: investor.investor_id,
                                page: 'investors-list',
                              })
                              handleInvestorDataChange({
                                ...investor,
                                investment_limit: String(Number(investor.investment_limit) / 100),
                              })
                              setDisplayModal(true)
                            }}
                            dataTestId='investor-table-edit-investment-limit-icon'
                          />
                        </span>
                      </td>
                      <td>
                        <EditIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() =>
                            handleInvestorProfileClick(investor.email, investor.investor_id)
                          }
                          dataTestId='investor-table-manage-profile-icon'
                        />
                      </td>
                      <td>
                        <EditIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() =>
                            handleInvestorInvestmentsClick(investor.email, investor.investor_id)
                          }
                          dataTestId='investor-table-investment-icon'
                        />
                      </td>
                      <td>
                        <EditIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() =>
                            handleInvestorWithdrawalClick(investor.email, investor.investor_id)
                          }
                          dataTestId='investor-table-withdrawal-icon'
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Flex>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </div>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Clients Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { InvestorsListPresenter }
